import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import {
  Modal,
  Text,
  Button,
  Group,
  Stack,
  Paper,
  Title,
  Alert,
} from "@mantine/core";

function GroupProgramModal({
  isOpen,
  onClose,
  group,
  groupPrograms = [],
  availablePrograms = [],
  onProgramsUpdated,
}) {
  const { getVerifiedJWT } = useAuth();
  const [loadingStates, setLoadingStates] = useState({});
  const [error, setError] = useState(null);
  const [localGroupPrograms, setLocalGroupPrograms] = useState(groupPrograms);
  const [localAvailablePrograms, setLocalAvailablePrograms] =
    useState(availablePrograms);
  const [hasChanges, setHasChanges] = useState(false);

  // Update local state when props change
  useEffect(() => {
    setLocalGroupPrograms(groupPrograms);
    setLocalAvailablePrograms(availablePrograms);
    // Reset changes flag when modal opens with new data
    setHasChanges(false);
  }, [groupPrograms, availablePrograms]);

  const updateGroupProgram = async (programId, include) => {
    if (!group?.id || !programId) return;

    const buttonKey = `${programId}-${include}`;
    setLoadingStates(prev => ({ ...prev, [buttonKey]: true }));
    setError(null);

    try {
      const jwt = await getVerifiedJWT();
      const response = await fetch(getApiEndpoint("/v1/groups/group/program"), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          group_id: group.id,
          program_id: programId,
          include: include,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to update group programs");
      }

      // Update local state immediately on success
      if (include) {
        // Adding program: move from available to group programs
        const programToAdd = localAvailablePrograms.find(
          program => program.id === programId
        );
        if (programToAdd) {
          setLocalGroupPrograms(prev => [...prev, programToAdd]);
          setLocalAvailablePrograms(prev =>
            prev.filter(program => program.id !== programId)
          );
        }
      } else {
        // Removing program: move from group programs to available
        const programToRemove = localGroupPrograms.find(
          program => program.id === programId
        );
        if (programToRemove) {
          setLocalAvailablePrograms(prev => [...prev, programToRemove]);
          setLocalGroupPrograms(prev =>
            prev.filter(program => program.id !== programId)
          );
        }
      }

      // Mark that changes have been made
      setHasChanges(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingStates(prev => ({ ...prev, [buttonKey]: false }));
    }
  };

  const handleAddProgram = program => {
    updateGroupProgram(program.id, true);
  };

  const handleRemoveProgram = program => {
    updateGroupProgram(program.id, false);
  };

  const handleClose = () => {
    setError(null);

    // Only call the refresh callback if changes were made
    if (hasChanges && onProgramsUpdated) {
      onProgramsUpdated();
    }

    onClose();
  };

  return (
    <Modal
      opened={isOpen}
      onClose={handleClose}
      title={`Edit Programs - ${group?.name || "Group"}`}
      size="lg"
      centered
    >
      <Stack gap="lg">
        {/* Error Alert */}
        {error && (
          <Alert variant="light" color="red" title="Error">
            {error}
          </Alert>
        )}

        {/* Group Programs Section */}
        <Paper p="md" withBorder>
          <Title order={4} mb="md">
            Group Programs ({localGroupPrograms.length})
          </Title>
          {localGroupPrograms.length > 0 ? (
            <Stack gap="sm">
              {localGroupPrograms.map((program, index) => (
                <Group
                  key={program.id || index}
                  gap="sm"
                  p="sm"
                  style={{
                    borderRadius: "8px",
                    backgroundColor: "var(--mantine-color-gray-0)",
                  }}
                >
                  <Stack gap={2} style={{ flex: 1 }}>
                    <Text fw={500}>
                      {program.program_name || "Unknown Program"}
                    </Text>
                    <Text size="sm" c="dimmed">
                      {program.description || "No description available"}
                    </Text>
                  </Stack>
                  <Button
                    size="xs"
                    variant="light"
                    color="red"
                    onClick={() => handleRemoveProgram(program)}
                    loading={loadingStates[`${program.id}-false`]}
                    disabled={Object.values(loadingStates).some(
                      loading => loading
                    )}
                  >
                    Remove
                  </Button>
                </Group>
              ))}
            </Stack>
          ) : (
            <Text c="dimmed" style={{ fontStyle: "italic" }}>
              No programs assigned to this group
            </Text>
          )}
        </Paper>

        {/* Available Programs Section */}
        <Paper p="md" withBorder>
          <Title order={4} mb="md">
            Available Programs ({localAvailablePrograms.length})
          </Title>
          {localAvailablePrograms.length > 0 ? (
            <Stack gap="sm">
              {localAvailablePrograms.map((program, index) => (
                <Group
                  key={program.id || index}
                  gap="sm"
                  p="sm"
                  style={{
                    borderRadius: "8px",
                    backgroundColor: "var(--mantine-color-gray-0)",
                  }}
                >
                  <Stack gap={2} style={{ flex: 1 }}>
                    <Text fw={500}>
                      {program.program_name || "Unknown Program"}
                    </Text>
                    <Text size="sm" c="dimmed">
                      {program.description || "No description available"}
                    </Text>
                  </Stack>
                  <Button
                    size="xs"
                    variant="light"
                    color="green"
                    onClick={() => handleAddProgram(program)}
                    loading={loadingStates[`${program.id}-true`]}
                    disabled={Object.values(loadingStates).some(
                      loading => loading
                    )}
                  >
                    Add
                  </Button>
                </Group>
              ))}
            </Stack>
          ) : (
            <Text c="dimmed" style={{ fontStyle: "italic" }}>
              No additional programs available
            </Text>
          )}
        </Paper>

        {/* Action Buttons */}
        <Group justify="flex-end" gap="sm">
          <Button variant="light" onClick={handleClose}>
            Close
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default GroupProgramModal;
